import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { ThemeContext } from 'styled-components';
import pdfIcon from 'assets/images/pdf-icon.png';
import { addPaddingZerosToNum, convertArabicNumbersToEnglish } from 'utils/functions/general';
import {
  getDateTimeObjFromDateTimeStr,
  getDateDetailsFromDateTimeObj,
  getTimeDetailsFromDateTimeObj,
  getAgeFromBirthDateObj,
} from 'utils/functions/dateTime';
import { status } from 'constants/general';
import { entityTypeEnum, patientGenderEnum } from 'modules/labs/common/enums';
import {
  orderStatusToShowUploadedResultsSection,
  orderAttachmentTypeEnum,
  orderResultsTypeEnum,
} from 'modules/labs/orders/enums';
import OrderDetailsHeader from 'modules/shared/orders/components/OrderDetailsHeader';
import OrderInvoice from 'modules/shared/orders/components/OrderInvoice';
import DropdownMenu from 'modules/shared/common/components/DropdownMenu';
import FileUploader from 'modules/shared/common/components/FileUploader';
import Loader from 'modules/shared/common/components/Loader';
import Popup from 'modules/shared/common/components/Popup';
import SectionDivider from 'modules/shared/common/components/SectionDivider';
import { format } from 'date-fns';
import Can from 'modules/shared/common/components/Can';
import Modal from 'modules/shared/common/components/Modal';
import InputField from 'modules/shared/common/components/InputField';
import {
  LoaderContainer,
  OrderDetailsTitle,
  OrderDetailsText,
  OrderDetailsSubText,
  /* ####################### */
  OrderDetailsNoDataText,
  OrderDetailsWrapper,
  OrderDetailsContent,
  OrderDetailsContentFirstColumn,
  OrderDetailsContentSecondColumn,
  OrderDetailsInfoSection,
  OrderDetailsLogsSection,
  OrderDetailsStatusSection,
  OrderDetailsChemistSection,
  OrderDetailsAttachmentSection,
  OrderDetailsAttachResultsSection,
  OrderDetailsNoteSection,
  /* ####################### */
  OrderDetailsInfoNumberSectionWrapper,
  OrderDetailsInfoNumberInfoWrapper,
  OrderDetailsInfoSticker,
  OrderDetailsInfoBadgesWrapper,
  OrderDetailsInfoBadges,
  OrderDetailsInfoPatientUserDetailsTitleWrapper,
  OrderDetailsInfoPatientUserDetailsTitleIcon,
  OrderDetailsInfoContent,
  OrderDetailsInfoImgWrapper,
  OrderDetailsInfoImg,
  OrderNumberOverlay,
  extendOrderDetailsPatientUserInfoLoaderStyle,
  /* ####################### */
  OrderDetailsStatusIconWrapper,
  OrderDetailsStatusIcon,
  OrderDetailsStatusTextWrapper,
  OrderDetailsStatusText,
  OrderDetailsStatusLine,
  /* ####################### */
  OrderDetailsChemistDropdownWrapper,
  /* ####################### */
  OrderDetailsAttachmentWrapper,
  OrderDetailsAttachmentImgWrapper,
  OrderDetailsAttachmentImgRatio,
  OrderDetailsAttachmentImg,
  OrderDetailsAttachmentImgFullSize,
  OrderDetailsAttachmentInfoWrapper,
  /* ####################### */
  OrderDetailsAttachResultsFilesWrapper,
  OrderDetailsAttachResultsFileItemWrapper,
  OrderDetailsAttachResultsFileItemCopyText,
  OrderDetailsAttachResultsFileItem,
  OrderDetailsAttachResultsFileItemPadding,
  OrderDetailsAttachResultsFileImg,
  OrderDetailsAttachResultsResizedFileImg,
  OrderDetailsAttachResultsImgPreview,
  OrderDetailsAttachResultsBtnWrapper,
  OrderDetailsAttachResultsBtn,
  extendOrderDetailsAttachResultsLoaderStyle,
  extendOrderDetailsAttachResultsBtnLoaderStyle,
  /* ####################### */
  OrderDetailsNoteTextArea,
  OrderDetailsNoteBtn,
  /* ####################### */
  OrderDetailsVisitNoteSection,
  OrdetVisitNote,
  FieldWrapper,
  FieldLabel,
  CompleteAddressField,
  FieldErrorLabel,
  ModalContentWrapper,
  AddressLabel,
  AddressLabelWrapper,
  /* ####################### */
} from './OrderDetailsComponent.style';

// ###############################################################
// <---------------------- Sub Sections ------------------------->
// ###############################################################
const OrderNumberSection = ({
  themeContext,
  stringsData,
  orderDetails,
  showOrderInfoBadges,
  showOrderInfoSticker,
  handleTextToCopyToClipboardClick,
  orderInfoStickerDetails,
  showPaymentMethodSticker,
}) => {
  const orderData = orderDetails.details;

  const { orderDate, orderNumber, externalKey } = orderData;

  const orderStateReason =
    orderData?.orderStates?.length > 0 &&
    orderData.orderStates[orderData.orderStates.length - 1].orderStateReason?.orderStateChildReason
      ?.name;

  let paymentStickerInfo = {};
  if (orderData.onlinePaymentCardKey) {
    paymentStickerInfo = {
      icon: "'\\e91a'",
      title: stringsData.ordersList.creditCardStickerText,
      color: 'purpleMain',
    };
  } else {
    paymentStickerInfo = {
      icon: "'\\e919'",
      title: stringsData.ordersList.cashStickerText,
      color: 'greenMain',
    };
  }
  return (
    <OrderDetailsInfoNumberSectionWrapper>
      <OrderDetailsInfoNumberInfoWrapper>
        <OrderDetailsTitle showCursor onClick={() => handleTextToCopyToClipboardClick(orderNumber)}>
          {`${stringsData.orderDetailsHeader.orderId}${orderData.orderNumber}`}
        </OrderDetailsTitle>
        <SectionDivider gap={4} />
        <OrderDetailsText>
          {`${orderDate.orderDateTimeStr} ${stringsData.orderDetailsContent.orderSource} ${orderData.source}`}
        </OrderDetailsText>
        <SectionDivider gap={4} />
        {orderDate.isOrderCreationDateSameAsDeliveryDate === false && (
          <OrderDetailsText>
            {`${stringsData.orderDetailsContent.orderScheduledDate} ${orderDate.orderScheduledDateTimeStr}`}
          </OrderDetailsText>
        )}
        <SectionDivider gap={4} />
        <OrderDetailsSubText>{orderDate.orderElapsedTimeStr}</OrderDetailsSubText>
        <SectionDivider gap={8} />
        <OrderDetailsText showCursor onClick={() => handleTextToCopyToClipboardClick(externalKey)}>
          {stringsData.orderDetailsContent.externalKey}
        </OrderDetailsText>
        {showOrderInfoBadges && (
          <>
            <SectionDivider gap={6} />
            <OrderDetailsInfoBadgesWrapper>
              {orderStateReason && (
                <OrderDetailsInfoBadges
                  textColor={themeContext.colors.redMain}
                  backgroundColor={themeContext.colors.redLight}
                >
                  {orderStateReason}
                </OrderDetailsInfoBadges>
              )}
              {orderData?.currentDeliveryDelayFlag && (
                <OrderDetailsInfoBadges
                  textColor={themeContext.colors.white}
                  backgroundColor={themeContext.colors.redMain}
                >
                  {stringsData.orderDetailsContent.deliveryDelay}
                </OrderDetailsInfoBadges>
              )}
              {orderData?.currentProcessingDelayFlag && (
                <OrderDetailsInfoBadges
                  textColor={themeContext.colors.white}
                  backgroundColor={themeContext.colors.redMain}
                >
                  {stringsData.orderDetailsContent.processingDelay}
                </OrderDetailsInfoBadges>
              )}
            </OrderDetailsInfoBadgesWrapper>
          </>
        )}
      </OrderDetailsInfoNumberInfoWrapper>
      {showOrderInfoSticker && (
        <OrderDetailsInfoSticker
          icon={orderInfoStickerDetails?.icon}
          color={themeContext.colors[orderInfoStickerDetails?.color]}
        >
          {orderInfoStickerDetails?.title}
        </OrderDetailsInfoSticker>
      )}
      {showPaymentMethodSticker && (
        <OrderDetailsInfoSticker
          icon={paymentStickerInfo?.icon}
          color={themeContext.colors[paymentStickerInfo?.color]}
        >
          {paymentStickerInfo?.title}
        </OrderDetailsInfoSticker>
      )}
    </OrderDetailsInfoNumberSectionWrapper>
  );
};
OrderNumberSection.propTypes = {
  themeContext: PropTypes.shape({
    colors: PropTypes.shape({
      redMain: PropTypes.string,
      redLight: PropTypes.string,
      white: PropTypes.string,
      greenMain: PropTypes.string,
    }),
  }).isRequired,
  stringsData: PropTypes.shape({
    orderDetailsHeader: PropTypes.shape({
      orderId: PropTypes.string,
    }),
    orderDetailsContent: PropTypes.shape({
      orderSource: PropTypes.string,
      orderScheduledDate: PropTypes.string,
      externalKey: PropTypes.string,
      resolveBadge: PropTypes.string,
      timeoutBadge: PropTypes.string,
      insuranceSticker: PropTypes.string,
      copiedToClipboardText: PropTypes.string,
      deliveryDelay: PropTypes.string,
      processingDelay: PropTypes.string,
    }),
    ordersList: PropTypes.shape({
      creditCardStickerText: PropTypes.string,
      cashStickerText: PropTypes.string,
    }),
  }).isRequired,
  orderDetails: PropTypes.shape({
    details: PropTypes.shape({
      orderNumber: PropTypes.string,
      externalKey: PropTypes.string,
      source: PropTypes.string,
      orderDate: PropTypes.shape({
        orderDateTimeStr: PropTypes.string,
        orderElapsedTimeStr: PropTypes.string,
        orderScheduledDateTimeStr: PropTypes.string,
        isOrderCreationDateSameAsDeliveryDate: PropTypes.bool,
      }),
      orderStates: PropTypes.arrayOf(
        PropTypes.shape({
          orderStateReason: PropTypes.shape({
            orderStateChildReason: PropTypes.shape({
              name: PropTypes.string,
            }),
          }),
        }),
      ),
      currentDeliveryDelayFlag: PropTypes.bool,
      currentProcessingDelayFlag: PropTypes.bool,
      onlinePaymentCardKey: PropTypes.string,
    }),
  }).isRequired,
  showOrderInfoBadges: PropTypes.bool,
  showOrderInfoSticker: PropTypes.bool,
  showPaymentMethodSticker: PropTypes.bool,
  handleTextToCopyToClipboardClick: PropTypes.func.isRequired,
  orderInfoStickerDetails: PropTypes.shape({
    icon: PropTypes.string,
    color: PropTypes.string,
    title: PropTypes.string,
  }),
};
OrderNumberSection.defaultProps = {
  showOrderInfoBadges: false,
  showOrderInfoSticker: false,
  showPaymentMethodSticker: false,
  orderInfoStickerDetails: {},
};

const PatientDetailsSection = ({ stringsData, orderDetails, showPatientDetailsEditBtn, handleTextToCopyToClipboardClick }) => {
  const orderData = orderDetails.details;
  const { patientDetails } = orderData;

  const patientGenderValue = patientGenderEnum.getKeyByValue(patientDetails.gender);
  const patientGender =
    stringsData.orderDetailsContent[`patientUserDetailsGender${patientGenderValue}`];

  return (
    <>
      <OrderDetailsInfoPatientUserDetailsTitleWrapper>
        <OrderDetailsTitle>{stringsData.orderDetailsContent.patientDetailsTitle}</OrderDetailsTitle>
        {showPatientDetailsEditBtn && <OrderDetailsInfoPatientUserDetailsTitleIcon />}
      </OrderDetailsInfoPatientUserDetailsTitleWrapper>
      <SectionDivider gap={4} />
      <OrderDetailsInfoContent>
        <OrderDetailsSubText>
          {stringsData.orderDetailsContent.patientDetailsName}
        </OrderDetailsSubText>
        <OrderDetailsText style={{ cursor: 'pointer' }} onClick={() => handleTextToCopyToClipboardClick(patientDetails.name)} blueColor>{patientDetails.name}</OrderDetailsText>
        <OrderDetailsSubText>
          {stringsData.orderDetailsContent.patientUserDetailsAge}
        </OrderDetailsSubText>
        <OrderDetailsText>{patientDetails.age || '-'}</OrderDetailsText>
        <OrderDetailsSubText>
          {stringsData.orderDetailsContent.patientUserDetailsGender}
        </OrderDetailsSubText>
        <OrderDetailsText>{patientGender}</OrderDetailsText>
      </OrderDetailsInfoContent>
    </>
  );
};
PatientDetailsSection.propTypes = {
  stringsData: PropTypes.shape({
    orderDetailsContent: PropTypes.shape({
      patientDetailsTitle: PropTypes.string,
      patientDetailsName: PropTypes.string,
      patientUserDetailsAge: PropTypes.string,
      patientUserDetailsGender: PropTypes.string,
      patientUserDetailsGenderMale: PropTypes.string,
      patientUserDetailsGenderFemale: PropTypes.string,
    }),
  }).isRequired,
  orderDetails: PropTypes.shape({
    details: PropTypes.shape({
      patientDetails: PropTypes.shape({
        name: PropTypes.string,
        age: PropTypes.number,
        gender: PropTypes.number,
      }),
    }),
  }).isRequired,
  showPatientDetailsEditBtn: PropTypes.bool,
};
PatientDetailsSection.defaultProps = {
  showPatientDetailsEditBtn: false,
};

const PatientUserDetailsSection = ({
  stringsData,
  orderDetails,
  showPatientUserDetailsEditBtn,
  handleTextToCopyToClipboardClick,
}) => {
  if (
    typeof orderDetails.details.patientUserDetails === 'string' &&
    orderDetails.details.patientUserDetails === status.FETCHING
  )
    return (
      <OrderDetailsInfoPatientUserDetailsTitleWrapper>
        <Loader
          width={50}
          height={50}
          thickness={6}
          extendStyle={extendOrderDetailsPatientUserInfoLoaderStyle}
        />
      </OrderDetailsInfoPatientUserDetailsTitleWrapper>
    );

  if (
    typeof orderDetails.details.patientUserDetails === 'string' &&
    orderDetails.details.patientUserDetails === status.FAIL
  )
    return (
      <OrderDetailsTitle>{stringsData.orderDetailsContent.noDataPlaceholder}</OrderDetailsTitle>
    );

  const orderData = orderDetails.details;
  const patientUserData = orderData.patientUserDetails;

  let patientUserAge = '';
  const patientUserBirthDate = patientUserData.Birthdate;
  if (patientUserBirthDate) {
    const { dateObj: patientUserBirthDateObj } = getDateTimeObjFromDateTimeStr(
      patientUserBirthDate,
      false,
      false,
      '/',
      true,
    );
    patientUserAge = getAgeFromBirthDateObj(patientUserBirthDateObj);
  }

  const patientUserGender =
    (patientUserData.Gender && stringsData.orderDetailsContent.patientUserDetailsGenderMale) ||
    stringsData.orderDetailsContent.patientUserDetailsGenderFemale;

  return (
    <>
      <OrderDetailsInfoPatientUserDetailsTitleWrapper>
        <OrderDetailsTitle>
          {stringsData.orderDetailsContent.patientUserDetailsTitle}
        </OrderDetailsTitle>
        {showPatientUserDetailsEditBtn && <OrderDetailsInfoPatientUserDetailsTitleIcon />}
      </OrderDetailsInfoPatientUserDetailsTitleWrapper>
      <SectionDivider gap={4} />
      <OrderDetailsInfoContent>
        <OrderDetailsSubText>
          {stringsData.orderDetailsContent.patientUserDetailsName}
        </OrderDetailsSubText>
        <OrderDetailsText style={{ cursor: 'pointer' }} onClick={() => handleTextToCopyToClipboardClick(patientUserData.Name)} blueColor>{patientUserData.Name}</OrderDetailsText>
        <OrderDetailsSubText>
          {stringsData.orderDetailsContent.patientUserDetailsPhoneNumber}
        </OrderDetailsSubText>
        <OrderDetailsText style={{ cursor: 'pointer' }} onClick={() => handleTextToCopyToClipboardClick(patientUserData.MobileNumber)} blueColor>{patientUserData.MobileNumber}</OrderDetailsText>
        <OrderDetailsSubText>
          {stringsData.orderDetailsContent.patientUserDetailsAge}
        </OrderDetailsSubText>
        <OrderDetailsText>{patientUserAge || '-'}</OrderDetailsText>
        <OrderDetailsSubText>
          {stringsData.orderDetailsContent.patientUserDetailsGender}
        </OrderDetailsSubText>
        <OrderDetailsText>{patientUserGender}</OrderDetailsText>
      </OrderDetailsInfoContent>
    </>
  );
};
PatientUserDetailsSection.propTypes = {
  stringsData: PropTypes.shape({
    orderDetailsContent: PropTypes.shape({
      noDataPlaceholder: PropTypes.string,
      patientUserDetailsTitle: PropTypes.string,
      patientUserDetailsName: PropTypes.string,
      patientUserDetailsPhoneNumber: PropTypes.string,
      patientUserDetailsAge: PropTypes.string,
      patientUserDetailsGender: PropTypes.string,
      patientUserDetailsGenderMale: PropTypes.string,
      patientUserDetailsGenderFemale: PropTypes.string,
    }),
  }).isRequired,
  orderDetails: PropTypes.shape({
    details: PropTypes.shape({
      patientUserDetails: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          Name: PropTypes.string,
          MobileNumber: PropTypes.string,
          Birthdate: PropTypes.string,
          Gender: PropTypes.bool,
        }),
      ]),
    }),
  }).isRequired,
  showPatientUserDetailsEditBtn: PropTypes.bool,
};
PatientUserDetailsSection.defaultProps = {
  showPatientUserDetailsEditBtn: false,
};

const AddressDetailsSection = ({
  stringsData,
  orderDetails,
  showAddressDetailsEditBtn,
  editShippingAddress,
  handleTextToCopyToClipboardClick
}) => {
  const orderData = orderDetails.details;
  const shippingAddressData = orderData.shippingAddressDTO;
  const buildingNumber = shippingAddressData.buildingNumber
    ? convertArabicNumbersToEnglish(shippingAddressData.buildingNumber)
    : '-';
  const flatNumber = shippingAddressData.flatNumber
    ? convertArabicNumbersToEnglish(shippingAddressData.flatNumber)
    : '-';
  const areaName = shippingAddressData.area?.areaTranslators[0].name;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmButtonLoading, setIsConfirmButtonLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shippingDetailsFieldsValues, setShippingDetailsFieldsValues] = useState({
    detail: shippingAddressData.detail,
    landmark: shippingAddressData.landmark,
    recipientNumber: shippingAddressData.recipientNumber,
    recipientName: shippingAddressData.recipientName,
    label: shippingAddressData.label,
    buildingNumber: shippingAddressData.buildingNumber,
    flatNumber: shippingAddressData.flatNumber,
  });
  const [errors, setErrors] = useState({});
  const [addressLabelArr, setAddressLabelArr] = useState([
    { id: 0, name: 'Home' },
    { id: 1, name: 'Work' },
    { id: 2, name: 'Hotel' },
    { id: 3, name: 'Others' },
  ]);

  const handleTextInputFieldChange = e => {
    const { name, value } = e.target;
    setShippingDetailsFieldsValues(prevValue => {
      const fieldsObj = { ...prevValue };
      fieldsObj[name] = value;
      return fieldsObj;
    });
  };

  const validateFields = fieldsToBeValidated => {
    const tempErrors = {};
    fieldsToBeValidated.forEach(field => {
      if (shippingDetailsFieldsValues[field] === '' || shippingDetailsFieldsValues[field] === null)
        tempErrors[field] = 'This field is required';
    });
    setErrors(tempErrors);
  };

  const clearModal = () => {
    setIsModalOpen(false);
    setIsConfirmButtonLoading(false);
    setIsSubmitting(false);
    setErrors({});
  };

  useEffect(() => {
    if (shippingAddressData?.label) {
      let tempAddressLabelArr = [...addressLabelArr];
      tempAddressLabelArr = tempAddressLabelArr.map(label => {
        const tempLabel = { ...label };
        tempLabel.selected = label.name === shippingAddressData?.label;
        return tempLabel;
      });
      setAddressLabelArr(tempAddressLabelArr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingAddressData]);

  useEffect(() => {
    if (isSubmitting && Object.keys(errors).length === 0) {
      setIsConfirmButtonLoading(true);
      editShippingAddress(shippingDetailsFieldsValues, shippingAddressData.key, clearModal);
    } else setIsSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  return (
    <>
      <OrderDetailsInfoPatientUserDetailsTitleWrapper>
        <OrderDetailsTitle>{stringsData.orderDetailsContent.addressDetailsTitle}</OrderDetailsTitle>
        {showAddressDetailsEditBtn && (
          <OrderDetailsInfoPatientUserDetailsTitleIcon onClick={() => setIsModalOpen(true)} />
        )}
      </OrderDetailsInfoPatientUserDetailsTitleWrapper>
      <SectionDivider gap={4} />
      <OrderDetailsInfoContent>
        <OrderDetailsSubText>
          {stringsData.orderDetailsContent.addressDetailsLabel}
        </OrderDetailsSubText>
        <OrderDetailsText>{shippingAddressData.label || '-'}</OrderDetailsText>
        <OrderDetailsSubText>
          {stringsData.orderDetailsContent.addressDetailsPatientName}
        </OrderDetailsSubText>
        <OrderDetailsText>{shippingAddressData.recipientName}</OrderDetailsText>
        <OrderDetailsSubText>
          {stringsData.orderDetailsContent.addressDetailsPhoneNumber}
        </OrderDetailsSubText>
        <OrderDetailsText>{shippingAddressData.recipientNumber}</OrderDetailsText>
        <OrderDetailsSubText>
          {stringsData.orderDetailsContent.addressDetailsName}
        </OrderDetailsSubText>
        <OrderDetailsText style={{ cursor: 'pointer' }} onClick={() => handleTextToCopyToClipboardClick(shippingAddressData.completeAddress)} blueColor>{shippingAddressData.completeAddress}</OrderDetailsText>
        <OrderDetailsSubText>
          {stringsData.orderDetailsContent.addressDetailsBuilding}
        </OrderDetailsSubText>
        <OrderDetailsText>{buildingNumber}</OrderDetailsText>
        <OrderDetailsSubText>
          {stringsData.orderDetailsContent.addressDetailsFlat}
        </OrderDetailsSubText>
        <OrderDetailsText>{flatNumber}</OrderDetailsText>
        <OrderDetailsSubText>
          {stringsData.orderDetailsContent.addressDetailsStreet}
        </OrderDetailsSubText>
        <OrderDetailsText>{shippingAddressData.detail || '-'}</OrderDetailsText>
        <OrderDetailsSubText>
          {stringsData.orderDetailsContent.addressDetailsArea}
        </OrderDetailsSubText>
        <OrderDetailsText>{areaName || '-'}</OrderDetailsText>
        <OrderDetailsSubText>
          {stringsData.orderDetailsContent.addressDetailsLandmark}
        </OrderDetailsSubText>
        <OrderDetailsText>{shippingAddressData.landmark || '-'}</OrderDetailsText>
      </OrderDetailsInfoContent>
      <Modal
        title={stringsData.orderDetailsContent.shippingAddressEditModalTitle}
        onClose={() => clearModal()}
        toggle={isModalOpen}
        onConfirmHandler={() => {
          const fieldsToBeValidated = [
            'detail',
            'buildingNumber',
            'flatNumber',
            'recipientName',
            'recipientNumber',
          ];
          validateFields(fieldsToBeValidated);
          setIsSubmitting(true);
        }}
        confirmActionLabel={stringsData.orderDetailsContent.EditModaConfirmBtnText}
        onCancelHandler={() => {
          setIsModalOpen(false);
        }}
        confirmActionLoading={isConfirmButtonLoading}
      >
        <ModalContentWrapper>
          <FieldWrapper>
            <FieldLabel>{stringsData.orderDetailsContent.addressDetailsCompleteAddress}</FieldLabel>
            <CompleteAddressField>{shippingAddressData.completeAddress}</CompleteAddressField>
          </FieldWrapper>
          <FieldWrapper>
            <FieldLabel>{stringsData.orderDetailsContent.addressDetailsStreet}</FieldLabel>
            <InputField
              type="text"
              name="detail"
              value={shippingDetailsFieldsValues.detail}
              onChange={e => handleTextInputFieldChange(e)}
              isValid={!errors?.detail}
            />
            <FieldErrorLabel>{errors?.detail}</FieldErrorLabel>
          </FieldWrapper>
          <FieldWrapper>
            <FieldLabel>{stringsData.orderDetailsContent.addressDetailsBuilding}</FieldLabel>
            <InputField
              type="text"
              name="buildingNumber"
              value={shippingDetailsFieldsValues.buildingNumber}
              onChange={e => handleTextInputFieldChange(e)}
              isValid={!errors?.buildingNumber}
            />
            <FieldErrorLabel>{errors?.buildingNumber}</FieldErrorLabel>
          </FieldWrapper>
          <FieldWrapper>
            <FieldLabel>{stringsData.orderDetailsContent.addressDetailsFlat}</FieldLabel>
            <InputField
              type="text"
              name="flatNumber"
              value={shippingDetailsFieldsValues.flatNumber}
              onChange={e => handleTextInputFieldChange(e)}
              isValid={!errors?.flatNumber}
            />
            <FieldErrorLabel>{errors?.flatNumber}</FieldErrorLabel>
          </FieldWrapper>
          <FieldWrapper>
            <FieldLabel>{stringsData.orderDetailsContent.addressDetailsLandmark}</FieldLabel>
            <InputField
              type="text"
              name="landmark"
              value={shippingDetailsFieldsValues.landmark}
              onChange={e => handleTextInputFieldChange(e)}
            />
          </FieldWrapper>
          <FieldWrapper>
            <FieldLabel>{stringsData.orderDetailsContent.addressDetailsLabel}</FieldLabel>
            <AddressLabelWrapper>
              {addressLabelArr.map(label => (
                <AddressLabel
                  key={label.id}
                  selected={label?.selected}
                  onClick={() => {
                    setShippingDetailsFieldsValues(prevValue => {
                      const fieldsObj = { ...prevValue };
                      fieldsObj.label = label.name;
                      return fieldsObj;
                    });
                    setAddressLabelArr(prevArrValue => {
                      const tempAddressLabelArr = [...prevArrValue];
                      const prevSelectedLabel = prevArrValue.find(
                        prevLabel => prevLabel.selected === true,
                      );
                      if (prevSelectedLabel)
                        tempAddressLabelArr[prevSelectedLabel.id].selected = false;
                      tempAddressLabelArr[label.id].selected = true;
                      return tempAddressLabelArr;
                    });
                  }}
                >
                  {label.name}
                </AddressLabel>
              ))}
            </AddressLabelWrapper>
          </FieldWrapper>
          <FieldWrapper>
            <FieldLabel>{stringsData.orderDetailsContent.addressDetailsPatientName}</FieldLabel>
            <InputField
              type="text"
              name="recipientName"
              value={shippingDetailsFieldsValues.recipientName}
              onChange={e => handleTextInputFieldChange(e)}
              isValid={!errors?.recipientName}
            />
            <FieldErrorLabel>{errors?.recipientName}</FieldErrorLabel>
          </FieldWrapper>
          <FieldWrapper>
            <FieldLabel>{stringsData.orderDetailsContent.addressDetailsPhoneNumber}</FieldLabel>
            <InputField
              type="text"
              name="recipientNumber"
              value={shippingDetailsFieldsValues.recipientNumber}
              onChange={e => handleTextInputFieldChange(e)}
              isValid={!errors?.recipientNumber}
            />
            <FieldErrorLabel>{errors?.recipientNumber}</FieldErrorLabel>
          </FieldWrapper>
        </ModalContentWrapper>
      </Modal>
    </>
  );
};
AddressDetailsSection.propTypes = {
  stringsData: PropTypes.shape({
    orderDetailsContent: PropTypes.shape({
      addressDetailsTitle: PropTypes.string,
      addressDetailsLabel: PropTypes.string,
      addressDetailsPatientName: PropTypes.string,
      addressDetailsPhoneNumber: PropTypes.string,
      addressDetailsName: PropTypes.string,
      addressDetailsBuilding: PropTypes.string,
      addressDetailsFlat: PropTypes.string,
      addressDetailsStreet: PropTypes.string,
      addressDetailsArea: PropTypes.string,
      addressDetailsCompleteAddress: PropTypes.string,
      addressDetailsLandmark: PropTypes.string,
      shippingAddressEditModalTitle: PropTypes.string,
      EditModaConfirmBtnText: PropTypes.string,
    }),
  }).isRequired,
  orderDetails: PropTypes.shape({
    details: PropTypes.shape({
      shippingAddressDTO: PropTypes.shape({
        label: PropTypes.string,
        recipientName: PropTypes.string,
        recipientNumber: PropTypes.string,
        completeAddress: PropTypes.string,
        buildingNumber: PropTypes.string,
        flatNumber: PropTypes.string,
        detail: PropTypes.string,
        area: PropTypes.shape({
          areaTranslators: PropTypes.arrayOf(
            PropTypes.shape({
              name: PropTypes.string,
            }),
          ),
        }),
        landmark: PropTypes.string,
        key: PropTypes.string,
      }),
    }),
  }).isRequired,
  showAddressDetailsEditBtn: PropTypes.bool,
  editShippingAddress: PropTypes.func,
};
AddressDetailsSection.defaultProps = {
  showAddressDetailsEditBtn: false,
  editShippingAddress: () => {},
};

const EntityDetailsSection = ({ stringsData, orderDetails }) => {
  if (
    typeof orderDetails.details.selectedLabObj === 'string' &&
    orderDetails.details.selectedLabObj === status.FETCHING
  )
    return (
      <OrderDetailsInfoPatientUserDetailsTitleWrapper>
        <Loader
          width={50}
          height={50}
          thickness={6}
          extendStyle={extendOrderDetailsPatientUserInfoLoaderStyle}
        />
      </OrderDetailsInfoPatientUserDetailsTitleWrapper>
    );

  if (
    typeof orderDetails.details.selectedLabObj === 'string' &&
    orderDetails.details.selectedLabObj === status.FAIL
  )
    return (
      <OrderDetailsTitle>{stringsData.orderDetailsContent.noDataPlaceholder}</OrderDetailsTitle>
    );

  const orderData = orderDetails.details;
  const { selectedLabObj, selectedChemistObj } = orderData;

  return (
    <>
      <OrderDetailsInfoPatientUserDetailsTitleWrapper>
        <OrderDetailsTitle>{stringsData.orderDetailsContent.entityDetailsTitle}</OrderDetailsTitle>
      </OrderDetailsInfoPatientUserDetailsTitleWrapper>
      <SectionDivider gap={4} />
      <OrderDetailsInfoContent>
        <OrderDetailsSubText>
          {stringsData.orderDetailsContent.entityDetailsLabName}
        </OrderDetailsSubText>
        <OrderDetailsText>
          {(selectedLabObj.key === '0' && '-') || selectedLabObj.name}
        </OrderDetailsText>
        <OrderDetailsSubText>
          {stringsData.orderDetailsContent.entityDetailsChemistName}
        </OrderDetailsSubText>
        <OrderDetailsText>
          {(selectedChemistObj.key === '0' && '-') || selectedChemistObj.name}
        </OrderDetailsText>
      </OrderDetailsInfoContent>
    </>
  );
};
EntityDetailsSection.propTypes = {
  stringsData: PropTypes.shape({
    orderDetailsContent: PropTypes.shape({
      noDataPlaceholder: PropTypes.string,
      entityDetailsTitle: PropTypes.string,
      entityDetailsLabName: PropTypes.string,
      entityDetailsChemistName: PropTypes.string,
    }),
  }).isRequired,
  orderDetails: PropTypes.shape({
    details: PropTypes.shape({
      selectedLabObj: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          key: PropTypes.string,
          name: PropTypes.string,
        }),
      ]),
      selectedChemistObj: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          key: PropTypes.string,
          name: PropTypes.string,
        }),
      ]),
    }),
  }).isRequired,
};

const InsuranceDetailsSection = ({ stringsData, orderDetails, insuranceProviders }) => {
  const orderData = orderDetails.details;
  const { patientInsurance } = orderData;
  return (
    <>
      <OrderDetailsTitle>{stringsData.orderDetailsContent.insuranceDetailsTitle}</OrderDetailsTitle>
      <SectionDivider gap={4} />
      <OrderDetailsInfoContent>
        <OrderDetailsSubText>
          {stringsData.orderDetailsContent.insuranceDetailsCompanyName}
        </OrderDetailsSubText>
        <OrderDetailsText>
          {insuranceProviders?.length > 0 && insuranceProviders[0]?.englishName}
        </OrderDetailsText>
        <OrderDetailsSubText>
          {stringsData.orderDetailsContent.insuranceDetailsCardHolder}
        </OrderDetailsSubText>
        <OrderDetailsText>{patientInsurance?.cardHolderName}</OrderDetailsText>
        <OrderDetailsSubText>
          {stringsData.orderDetailsContent.insuranceDetailsPatientInsId}
        </OrderDetailsSubText>
        <OrderDetailsText>{patientInsurance?.memberID}</OrderDetailsText>
        <OrderDetailsSubText>
          {stringsData.orderDetailsContent.insuranceDetailsBirthDate}
        </OrderDetailsSubText>
        <OrderDetailsText>
          {format(new Date(patientInsurance?.cardHolderBirthdate), 'MMM dd, yyyy')}
        </OrderDetailsText>
        <OrderDetailsSubText>
          {stringsData.orderDetailsContent.insuranceDetailsIdExpiryDate}
        </OrderDetailsSubText>
        <OrderDetailsText>
          {format(new Date(patientInsurance?.expiryDate), 'MMM dd, yyyy')}
        </OrderDetailsText>
      </OrderDetailsInfoContent>
      <SectionDivider gap={16} />
      <OrderDetailsInfoImgWrapper>
        <OrderDetailsInfoImg src={patientInsurance?.cardImageUrl} alt="insurance" />
      </OrderDetailsInfoImgWrapper>
    </>
  );
};
InsuranceDetailsSection.propTypes = {
  stringsData: PropTypes.shape({
    orderDetailsContent: PropTypes.shape({
      insuranceDetailsTitle: PropTypes.string,
      insuranceDetailsCompanyName: PropTypes.string,
      insuranceDetailsCardHolder: PropTypes.string,
      insuranceDetailsPatientInsId: PropTypes.string,
      insuranceDetailsBirthDate: PropTypes.string,
      insuranceDetailsIdExpiryDate: PropTypes.string,
    }),
  }).isRequired,
  orderDetails: PropTypes.shape({
    details: PropTypes.shape({
      patientInsurance: PropTypes.shape({
        cardHolderName: PropTypes.string,
        cardHolderBirthdate: PropTypes.string,
        memberID: PropTypes.string,
        expiryDate: PropTypes.string,
        cardImageUrl: PropTypes.string,
      }),
    }),
  }).isRequired,
  insuranceProviders: PropTypes.arrayOf({}).isRequired,
};

const OrderInfoSection = props => {
  const { themeContext, orderDetails, showInsuranceDetailsSection, permissionsCategories } = props;
  const orderData = orderDetails.details;
  const { patientDetails, shippingAddressDTO: shippingAddressData } = orderData;

  return (
    <OrderDetailsInfoSection>
      <OrderNumberSection {...props} />
      {patientDetails && (
        <Can
          permissionsCategory={permissionsCategories?.order}
          perform="order:viewPatientDetails"
          renderYes={() => (
            <>
              <SectionDivider
                gap={16}
                withSeparator
                separatorGap={16}
                borderColor={themeContext.colors.grey004}
              />
              <PatientDetailsSection {...props} />
            </>
          )}
          renderNo={() => ''}
        />
      )}
      <SectionDivider
        gap={16}
        withSeparator
        separatorGap={16}
        borderColor={themeContext.colors.grey004}
      />
      <PatientUserDetailsSection {...props} />
      {shippingAddressData && (
        <Can
          permissionsCategory={permissionsCategories?.order}
          perform="order:viewShippingAddress"
          renderYes={() => (
            <>
              <SectionDivider
                gap={16}
                withSeparator
                separatorGap={16}
                borderColor={themeContext.colors.grey004}
              />
              <AddressDetailsSection {...props} />
            </>
          )}
          renderNo={() => ''}
        />
      )}
      <SectionDivider
        gap={16}
        withSeparator
        separatorGap={16}
        borderColor={themeContext.colors.grey004}
      />
      <EntityDetailsSection {...props} />
      {showInsuranceDetailsSection && (
        <Can
          permissionsCategory={permissionsCategories?.order}
          perform="order:viewInsuranceDetails"
          renderYes={() => (
            <>
              <SectionDivider
                gap={16}
                withSeparator
                separatorGap={16}
                borderColor={themeContext.colors.grey004}
              />
              <InsuranceDetailsSection {...props} />
            </>
          )}
          renderNo={() => ''}
        />
      )}
    </OrderDetailsInfoSection>
  );
};
OrderInfoSection.propTypes = {
  themeContext: PropTypes.shape({
    colors: PropTypes.shape({
      grey004: PropTypes.string,
    }),
  }).isRequired,
  orderDetails: PropTypes.shape({
    details: PropTypes.shape({
      patientDetails: PropTypes.shape({}),
      shippingAddressDTO: PropTypes.shape({}),
    }),
  }).isRequired,
  showInsuranceDetailsSection: PropTypes.bool,
  permissionsCategories: PropTypes.shape({
    order: PropTypes.string,
  }),
};
OrderInfoSection.defaultProps = {
  showInsuranceDetailsSection: false,
  permissionsCategories: {},
};

const OrderLog = ({ stringsData, orderLogObj, orderNumber, orderStatusEnum }) => {
  const {
    orderAssignedLab,
    orderAssignedChemist,
    actionUser,
    orderLogStatus,
    orderLogDate,
    orderLogTime,
    orderStateTypeId,
    orderScheduledToDate,
    orderScheduledToTime,
    scheduleOrderReason,
    cancelOrderReason,
  } = orderLogObj;
  const assignedToTxt = orderAssignedLab || orderAssignedChemist || '';
  return (
    <>
      <OrderDetailsText>
        {stringsData.orderDetailsContent.orderLogsEntry(orderNumber, orderLogStatus, assignedToTxt)}
      </OrderDetailsText>
      <OrderDetailsSubText>
        {stringsData.orderDetailsContent.orderLogsUser(actionUser, orderLogDate, orderLogTime)}
      </OrderDetailsSubText>
      {orderStateTypeId === orderStatusEnum.Scheduled && (
        <>
          <OrderDetailsSubText>
            {stringsData.orderDetailsContent.orderScheduled(
              orderScheduledToDate,
              orderScheduledToTime,
            )}
          </OrderDetailsSubText>
          <OrderDetailsSubText>{scheduleOrderReason}</OrderDetailsSubText>
        </>
      )}
      {orderStateTypeId === orderStatusEnum.Cancelled && (
        <OrderDetailsSubText>{cancelOrderReason}</OrderDetailsSubText>
      )}
    </>
  );
};
OrderLog.propTypes = {
  stringsData: PropTypes.shape({
    orderDetailsContent: PropTypes.shape({
      orderLogsEntry: PropTypes.func,
      orderLogsUser: PropTypes.func,
      orderScheduled: PropTypes.func,
    }),
  }).isRequired,
  orderLogObj: PropTypes.shape({
    orderAssignedLab: PropTypes.string,
    orderAssignedChemist: PropTypes.string,
    actionUser: PropTypes.string,
    orderLogStatus: PropTypes.string,
    orderLogDate: PropTypes.string,
    orderLogTime: PropTypes.string,
    orderStateTypeId: PropTypes.number,
    orderScheduledToTime: PropTypes.string,
    orderScheduledToDate: PropTypes.string,
    scheduleOrderReason: PropTypes.string,
    cancelOrderReason: PropTypes.string,
  }).isRequired,
  orderNumber: PropTypes.string.isRequired,
  orderStatusEnum: PropTypes.shape({
    Scheduled: PropTypes.number,
    Cancelled: PropTypes.number,
  }).isRequired,
};

const OrderLogsSection = props => {
  const {
    themeContext,
    stringsData,
    orderDetails,
    labsList,
    chemistsList,
    usersList,
    orderStatusEnum,
  } = props;

  if (
    labsList.status === status.SHOULD_CALL_API ||
    chemistsList.status === status.SHOULD_CALL_API ||
    usersList.status === status.SHOULD_CALL_API ||
    labsList.status === status.FETCHING ||
    chemistsList.status === status.FETCHING ||
    usersList.status === status.FETCHING
  )
    return (
      <OrderDetailsLogsSection>
        <Loader
          width={50}
          height={50}
          thickness={6}
          extendStyle={extendOrderDetailsPatientUserInfoLoaderStyle}
        />
      </OrderDetailsLogsSection>
    );

  if (
    labsList.status === status.FAIL ||
    chemistsList.status === status.FAIL ||
    usersList.status === status.FAIL
  )
    return (
      <OrderDetailsLogsSection>
        <OrderDetailsTitle>{stringsData.orderDetailsContent.noDataPlaceholder}</OrderDetailsTitle>
      </OrderDetailsLogsSection>
    );

  const orderData = orderDetails.details;
  const orderLogs = orderData.orderStates;
  const { orderNumber } = orderData;

  const allLabs = labsList.details;
  const allChemists = chemistsList.details.results;
  const allUsers = usersList.details.results;

  const mappedOrderLogs = orderLogs.map(orderLog => {
    const { key: orderLogKey, timeStamp: orderLogDateTime } = orderLog;

    let orderAssignedLab = '';
    let orderAssignedChemist = '';

    const orderAssignedEntity = orderLog.assignedEntity;

    if (orderAssignedEntity) {
      orderAssignedLab =
        (
          orderAssignedEntity.entityTypeId === entityTypeEnum.Lab &&
          allLabs.find(lab => lab.key === orderAssignedEntity.entityId)
        )?.name || '';

      orderAssignedChemist =
        (
          orderAssignedEntity.entityTypeId === entityTypeEnum.Chemist &&
          allChemists.find(chemist => chemist.key === orderAssignedEntity.entityId)
        )?.fullName || '';
    }

    const actionUser = allUsers.find(user => user.key === orderLog.entityId)?.fullName || '';

    const orderLogStatus = orderStatusEnum.getKeyByValue(orderLog.orderStateTypeId);

    const { dateObj: orderLogDateObj } = getDateTimeObjFromDateTimeStr(
      orderLog.timeStamp,
      true,
      true,
    );
    const {
      day: orderLogDay,
      month: orderLogMonth,
      year: orderLogYear,
    } = getDateDetailsFromDateTimeObj(orderLogDateObj);
    const orderLogDate = `${orderLogDay} ${orderLogMonth} ${orderLogYear}`;
    const {
      hours: orderLogHours,
      minutes: orderLogMinutes,
      period: orderLogPeriod,
    } = getTimeDetailsFromDateTimeObj(orderLogDateObj);
    const orderLogTime = `${orderLogHours}:${addPaddingZerosToNum(
      orderLogMinutes,
      1,
      2,
    )}${orderLogPeriod}`;

    if (orderLog.scheduledTo) {
      const { dateObj: orderScheduledToDateObj } = getDateTimeObjFromDateTimeStr(
        orderLog.scheduledTo,
        true,
        true,
      );
      const {
        day: orderScheduledToDay,
        month: orderScheduledToMonth,
        year: orderScheduledToYear,
      } = getDateDetailsFromDateTimeObj(orderScheduledToDateObj);
      const orderScheduledToDate = `${orderScheduledToDay} ${orderScheduledToMonth} ${orderScheduledToYear}`;
      const {
        hours: orderScheduledToHours,
        minutes: orderScheduledToMinutes,
        period: orderScheduledToPeriod,
      } = getTimeDetailsFromDateTimeObj(orderScheduledToDateObj);
      const orderScheduledToTime = `${orderScheduledToHours}:${addPaddingZerosToNum(
        orderScheduledToMinutes,
        1,
        2,
      )}${orderScheduledToPeriod}`;

      return {
        orderLogKey,
        orderAssignedLab,
        orderAssignedChemist,
        actionUser,
        orderLogStatus,
        orderLogDateTime,
        orderLogDate,
        orderLogTime,
        orderScheduledToDate,
        orderScheduledToTime,
        orderStateTypeId: orderLog.orderStateTypeId,
        scheduleOrderReason: orderLog.orderStateReason?.orderStateChildReason?.name,
      };
    }

    return {
      orderLogKey,
      orderAssignedLab,
      orderAssignedChemist,
      actionUser,
      orderLogStatus,
      orderLogDateTime,
      orderLogDate,
      orderLogTime,
      orderStateTypeId: orderLog.orderStateTypeId,
      cancelOrderReason:
        orderLog.orderStateTypeId === orderStatusEnum.Cancelled
          ? orderLog.orderStateReason?.orderStateChildReason?.name
          : null,
    };
  });

  return (
    <OrderDetailsLogsSection>
      <OrderDetailsTitle>{stringsData.orderDetailsContent.orderLogsTitle}</OrderDetailsTitle>
      <SectionDivider
        gap={8}
        withSeparator
        separatorGap={8}
        borderColor={themeContext.colors.grey004}
      />
      {mappedOrderLogs
        .sort((a, b) => {
          if (a.orderLogDateTime < b.orderLogDateTime) return 1;
          if (a.orderLogDateTime > b.orderLogDateTime) return -1;
          return 0;
        })
        .map((orderLogObj, index) => {
          const isLastLog = index === mappedOrderLogs.length - 1;

          return (
            <React.Fragment key={orderLogObj.orderLogKey}>
              <OrderLog
                orderLogObj={orderLogObj}
                orderNumber={orderNumber}
                orderStatusEnum={orderStatusEnum}
                {...props}
              />
              {isLastLog === false && (
                <SectionDivider
                  gap={8}
                  withSeparator
                  separatorGap={8}
                  borderColor={themeContext.colors.grey004}
                />
              )}
            </React.Fragment>
          );
        })}
    </OrderDetailsLogsSection>
  );
};
OrderLogsSection.propTypes = {
  themeContext: PropTypes.shape({
    colors: PropTypes.shape({
      grey004: PropTypes.string,
    }),
  }).isRequired,
  stringsData: PropTypes.shape({
    orderDetailsContent: PropTypes.shape({
      noDataPlaceholder: PropTypes.string,
      orderLogsTitle: PropTypes.string,
    }),
  }).isRequired,
  orderDetails: PropTypes.shape({
    details: PropTypes.shape({
      orderNumber: PropTypes.string,
      orderStates: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }).isRequired,
  labsList: PropTypes.shape({
    status: PropTypes.string,
    details: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  chemistsList: PropTypes.shape({
    status: PropTypes.string,
    details: PropTypes.shape({
      results: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }).isRequired,
  usersList: PropTypes.shape({
    status: PropTypes.string,
    details: PropTypes.shape({
      results: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }).isRequired,
  orderStatusEnum: PropTypes.shape({
    getKeyByValue: PropTypes.func,
    Cancelled: PropTypes.number,
  }).isRequired,
};

const OrderStatus = ({ orderStatusObj, isLastStatus }) => (
  <>
    <OrderDetailsStatusIconWrapper>
      <OrderDetailsStatusIcon color={orderStatusObj.orderStatusColor} />
      <OrderDetailsStatusTextWrapper>
        <OrderDetailsStatusText color={orderStatusObj.orderStatusColor}>
          {orderStatusObj.orderStatusName}
        </OrderDetailsStatusText>
      </OrderDetailsStatusTextWrapper>
    </OrderDetailsStatusIconWrapper>
    {isLastStatus === false && <OrderDetailsStatusLine color={orderStatusObj.orderStatusColor} />}
  </>
);
OrderStatus.propTypes = {
  orderStatusObj: PropTypes.shape({
    orderStatusName: PropTypes.string,
    orderStatusColor: PropTypes.string,
  }).isRequired,
  isLastStatus: PropTypes.bool.isRequired,
};

const OrderStatusSection = props => {
  const { mappedOrderStatus } = props;
  return (
    <OrderDetailsStatusSection>
      {mappedOrderStatus.map((orderStatusObj, index) => {
        const isLastStatus = index === mappedOrderStatus.length - 1;

        return (
          <OrderStatus
            key={`${orderStatusObj.orderStatusName} ${Math.random().toString()}`}
            orderStatusObj={orderStatusObj}
            isLastStatus={isLastStatus}
          />
        );
      })}
    </OrderDetailsStatusSection>
  );
};
OrderStatusSection.propTypes = {
  mappedOrderStatus: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

const OrderAssignedEntitySection = ({
  stringsData,
  labAssignDropdownMenuItemsList,
  labAssignDropdownMenuSelectedObj,
  assignOrderToEntityDetails,
  showAssignChemistDropdown,
  handleLabAssignDropdownMenuChange,
  permissionsCategories,
}) => {
  if (
    typeof labAssignDropdownMenuSelectedObj === 'string' &&
    labAssignDropdownMenuSelectedObj === status.FETCHING
  )
    return (
      <OrderDetailsChemistSection>
        <Loader
          width={50}
          height={50}
          thickness={6}
          extendStyle={extendOrderDetailsPatientUserInfoLoaderStyle}
        />
      </OrderDetailsChemistSection>
    );

  if (
    typeof labAssignDropdownMenuSelectedObj === 'string' &&
    labAssignDropdownMenuSelectedObj === status.FAIL
  )
    return (
      <OrderDetailsChemistSection>
        <OrderDetailsTitle>{stringsData.orderDetailsContent.noDataPlaceholder}</OrderDetailsTitle>
      </OrderDetailsChemistSection>
    );

  const activeLabAssignDropdownMenuItemsList = labAssignDropdownMenuItemsList.details.filter(
    lab => lab.isActive,
  );

  return (
    <OrderDetailsChemistSection>
      <Can
        permissionsCategory={permissionsCategories?.order}
        perform="order:assignPharmacy"
        renderYes={() => (
          <OrderDetailsChemistDropdownWrapper>
            <OrderDetailsTitle>
              {stringsData.orderDetailsContent.orderLabDropdownTitle}
            </OrderDetailsTitle>
            <SectionDivider gap={4} />
            <DropdownMenu
              dropdownMenuItemsList={activeLabAssignDropdownMenuItemsList}
              dropdownMenuSelectedObj={labAssignDropdownMenuSelectedObj}
              showLoader={assignOrderToEntityDetails.status === status.FETCHING}
              handleDropdownMenuChange={handleLabAssignDropdownMenuChange}
              objectMapper={{ key: 'key', name: 'name' }}
            />
          </OrderDetailsChemistDropdownWrapper>
        )}
        renderNo={() => null}
      />
      {showAssignChemistDropdown && (
        <OrderDetailsChemistDropdownWrapper>
          <OrderDetailsTitle>
            {stringsData.orderDetailsContent.orderChemistDropdownTitle}
          </OrderDetailsTitle>
          <SectionDivider gap={4} />
          <DropdownMenu />
        </OrderDetailsChemistDropdownWrapper>
      )}
    </OrderDetailsChemistSection>
  );
};
OrderAssignedEntitySection.propTypes = {
  showAssignChemistDropdown: PropTypes.bool,
  stringsData: PropTypes.shape({
    orderDetailsContent: PropTypes.shape({
      noDataPlaceholder: PropTypes.string,
      orderLabDropdownTitle: PropTypes.string,
      orderChemistDropdownTitle: PropTypes.string,
    }),
  }).isRequired,
  labAssignDropdownMenuItemsList: PropTypes.shape({
    details: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  labAssignDropdownMenuSelectedObj: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})])
    .isRequired,
  assignOrderToEntityDetails: PropTypes.shape({
    status: PropTypes.string,
  }).isRequired,
  handleLabAssignDropdownMenuChange: PropTypes.func.isRequired,
  permissionsCategories: PropTypes.shape({
    order: PropTypes.string,
  }),
};
OrderAssignedEntitySection.defaultProps = {
  showAssignChemistDropdown: false,
  permissionsCategories: {},
};

const OrderAttachment = ({ themeContext, stringsData, orderDetails, attachment, isLastItem }) => {
  const [isOrderAttachementImgPopupOpen, setIsOrderAttachementImgPopupOpen] = useState(false);

  const handleOrderAttachementImgClick = () => {
    setIsOrderAttachementImgPopupOpen(true);
  };

  const handleOrderAttachementImgPopupCloseBtnClick = () => {
    setIsOrderAttachementImgPopupOpen(false);
  };

  const orderData = orderDetails.details;
  const orderSource = orderData.source;
  const showImg = attachment.attachmentType === orderAttachmentTypeEnum.image;

  return (
    <>
      <OrderDetailsAttachmentWrapper>
        {showImg && (
          <OrderDetailsAttachmentImgWrapper onClick={() => handleOrderAttachementImgClick()}>
            <OrderDetailsAttachmentImgRatio />
            <OrderDetailsAttachmentImg src={attachment.url} alt={attachment.key} />
          </OrderDetailsAttachmentImgWrapper>
        )}
        <OrderDetailsAttachmentInfoWrapper>
          <OrderDetailsTitle>
            {stringsData.orderDetailsContent.orderAttachmentInfoTitle}
          </OrderDetailsTitle>
          <OrderDetailsSubText>
            {`${stringsData.orderDetailsContent.orderAttachmentInfoFrom} ${orderSource}`}
          </OrderDetailsSubText>
          <OrderDetailsText>{attachment.note}</OrderDetailsText>
        </OrderDetailsAttachmentInfoWrapper>
      </OrderDetailsAttachmentWrapper>
      {isLastItem === false && (
        <SectionDivider
          gap={16}
          withSeparator
          separatorGap={16}
          borderColor={themeContext.colors.grey004}
        />
      )}
      {isOrderAttachementImgPopupOpen && (
        <Popup handleCloseBtnClick={() => handleOrderAttachementImgPopupCloseBtnClick()}>
          <OrderDetailsAttachmentImgFullSize src={attachment.url} alt={attachment.key} />
        </Popup>
      )}
    </>
  );
};
OrderAttachment.propTypes = {
  themeContext: PropTypes.shape({
    colors: PropTypes.shape({
      grey004: PropTypes.string,
    }),
  }).isRequired,
  stringsData: PropTypes.shape({
    orderDetailsContent: PropTypes.shape({
      orderAttachmentInfoTitle: PropTypes.string,
      orderAttachmentInfoFrom: PropTypes.string,
    }),
  }).isRequired,
  orderDetails: PropTypes.shape({
    details: PropTypes.shape({
      source: PropTypes.string,
    }),
  }).isRequired,
  attachment: PropTypes.shape({
    key: PropTypes.string,
    url: PropTypes.string,
    note: PropTypes.string,
    attachmentType: PropTypes.string,
  }).isRequired,
  isLastItem: PropTypes.bool.isRequired,
};

const OrderAttachmentSection = ({ themeContext, stringsData, orderDetails }) => {
  const orderData = orderDetails.details;
  const { orderAttachments } = orderData;

  if (orderAttachments.length === 0) return '';

  return (
    <OrderDetailsAttachmentSection>
      {orderAttachments.map((attachment, index) => {
        const isLastItem = index === orderAttachments.length - 1;

        return (
          <OrderAttachment
            key={attachment.key}
            themeContext={themeContext}
            stringsData={stringsData}
            orderDetails={orderDetails}
            attachment={attachment}
            isLastItem={isLastItem}
          />
        );
      })}
    </OrderDetailsAttachmentSection>
  );
};
OrderAttachmentSection.propTypes = {
  themeContext: PropTypes.shape({
    colors: PropTypes.shape({
      grey004: PropTypes.string,
    }),
  }).isRequired,
  stringsData: PropTypes.shape({
    orderDetailsContent: PropTypes.shape({
      orderAttachmentInfoTitle: PropTypes.string,
      orderAttachmentInfoFrom: PropTypes.string,
    }),
  }).isRequired,
  orderDetails: PropTypes.shape({
    details: PropTypes.shape({
      source: PropTypes.string,
      orderAttachments: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }).isRequired,
};

const FileItem = ({ file, togglePreviewImg }) => {
  const { type, dimensions } = file;
  let { url } = file;
  const { dimenParamToChange, ratio } = dimensions || {};

  const isImg = type === orderResultsTypeEnum.image;

  let fileOpacity = 0.6;
  let fileBlur = 3;
  if (isImg === false) {
    url = pdfIcon;
    fileOpacity = 1;
    fileBlur = 0;
  }

  return (
    <OrderDetailsAttachResultsFileItem onClick={() => togglePreviewImg(true, file)}>
      <OrderDetailsAttachResultsFileItemPadding />
      <OrderDetailsAttachResultsFileImg src={url} opacity={fileOpacity} blur={fileBlur} />
      {isImg && (
        <OrderDetailsAttachResultsResizedFileImg
          src={url}
          dimenParamToChange={dimenParamToChange}
          ratio={ratio}
        />
      )}
    </OrderDetailsAttachResultsFileItem>
  );
};
FileItem.propTypes = {
  file: PropTypes.shape({
    type: PropTypes.string,
    url: PropTypes.string,
    dimensions: PropTypes.shape({
      dimenParamToChange: PropTypes.string,
      ratio: PropTypes.number,
    }),
  }).isRequired,
  togglePreviewImg: PropTypes.func.isRequired,
};

const OrderAttachResultsSection = ({
  themeContext,
  stringsData,
  orderDetails,
  uploadContentSubmission,
  orderResultsArr,
  previewFileIndex,
  isPreviewFilePopupOpen,
  setOrderResultsArr,
  togglePreviewImg,
  handleTextToCopyToClipboardClick,
  handleOrderSendResultsBtnClick,
  orderStatusEnum,
}) => {
  const orderData = orderDetails.details;
  const { attachedResults, orderStateTypeId: orderStatus } = orderData;
  const attachedResultsArr = attachedResults?.attachments || [];

  const imgPreviewFile = attachedResultsArr[previewFileIndex];

  return (
    <OrderDetailsAttachResultsSection>
      <OrderDetailsTitle>
        {stringsData.orderDetailsContent.orderAttachResultsTitle}
      </OrderDetailsTitle>
      <SectionDivider gap={16} />
      {orderStatusToShowUploadedResultsSection.includes(orderStatus) &&
        typeof attachedResults !== 'string' &&
        attachedResultsArr?.length > 0 && (
          <>
            <OrderDetailsAttachResultsFilesWrapper>
              {attachedResultsArr.map(file => (
                <OrderDetailsAttachResultsFileItemWrapper key={file.name}>
                  <FileItem key={file.name} file={file} togglePreviewImg={togglePreviewImg} />
                  <SectionDivider gap={8} />
                  <OrderDetailsAttachResultsFileItemCopyText
                    onClick={() => handleTextToCopyToClipboardClick(file.url)}
                  >
                    {stringsData.orderDetailsContent.orderAttachResultsCopyToClipboardText}
                  </OrderDetailsAttachResultsFileItemCopyText>
                </OrderDetailsAttachResultsFileItemWrapper>
              ))}
            </OrderDetailsAttachResultsFilesWrapper>
            {isPreviewFilePopupOpen && (
              <Popup handleCloseBtnClick={() => togglePreviewImg()}>
                <OrderDetailsAttachResultsImgPreview src={imgPreviewFile.url} />
              </Popup>
            )}
            <SectionDivider
              gap={16}
              withSeparator
              separatorGap={16}
              borderColor={themeContext.colors.grey004}
            />
          </>
        )}
      {orderStatusToShowUploadedResultsSection.includes(orderStatus) &&
        typeof attachedResults === 'string' &&
        (attachedResults === status.SHOULD_CALL_API || attachedResults === status.FETCHING) && (
          <Loader
            width={40}
            height={40}
            thickness={6}
            extendStyle={extendOrderDetailsAttachResultsLoaderStyle}
          />
        )}
      {/* TODO: remove 'true' */}
      {orderStatus === orderStatusEnum['Sample Dropped'] && (
        <>
          <FileUploader orderResultsArr={orderResultsArr} setUploadedData={setOrderResultsArr} />
          <SectionDivider gap={8} />
          <OrderDetailsAttachResultsBtnWrapper>
            <OrderDetailsAttachResultsBtn
              as="button"
              onClick={() => handleOrderSendResultsBtnClick(orderResultsArr)}
            >
              {(uploadContentSubmission.status === status.FETCHING && (
                <Loader
                  width={14}
                  height={14}
                  thickness={4}
                  extendStyle={extendOrderDetailsAttachResultsBtnLoaderStyle}
                />
              )) ||
                stringsData.orderDetailsContent.orderAttachResultsBtnText}
            </OrderDetailsAttachResultsBtn>
          </OrderDetailsAttachResultsBtnWrapper>
        </>
      )}
    </OrderDetailsAttachResultsSection>
  );
};
OrderAttachResultsSection.propTypes = {
  themeContext: PropTypes.shape({
    colors: PropTypes.shape({
      grey004: PropTypes.string,
    }),
  }).isRequired,
  stringsData: PropTypes.shape({
    orderDetailsContent: PropTypes.shape({
      orderAttachResultsTitle: PropTypes.string,
      orderAttachResultsCopyToClipboardText: PropTypes.string,
      orderAttachResultsBtnText: PropTypes.string,
    }),
  }).isRequired,
  orderDetails: PropTypes.shape({
    details: PropTypes.shape({
      orderStateTypeId: PropTypes.number,
      attachedResults: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({ attachments: PropTypes.arrayOf(PropTypes.shape({})) }),
      ]),
    }),
  }).isRequired,
  uploadContentSubmission: PropTypes.shape({
    status: PropTypes.string,
  }).isRequired,
  orderResultsArr: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  previewFileIndex: PropTypes.number.isRequired,
  isPreviewFilePopupOpen: PropTypes.bool.isRequired,
  togglePreviewImg: PropTypes.func.isRequired,
  setOrderResultsArr: PropTypes.func.isRequired,
  handleTextToCopyToClipboardClick: PropTypes.func.isRequired,
  handleOrderSendResultsBtnClick: PropTypes.func.isRequired,
  orderStatusEnum: PropTypes.shape({
    'Sample Dropped': PropTypes.number,
  }).isRequired,
};

const OrderNoteSection = ({
  stringsData,
  orderNoteChangeSubmission,
  orderNoteValue,
  handleOrderNoteFieldChange,
  handleOrderNoteSubmitBtnClick,
}) => (
  <OrderDetailsNoteSection>
    <OrderDetailsTitle>{stringsData.orderDetailsContent.orderNoteTitle}</OrderDetailsTitle>
    <SectionDivider gap={8} />
    <OrderDetailsNoteTextArea
      placeholder={stringsData.orderDetailsContent.orderNoteTextAreaPlaceholder}
      value={orderNoteValue}
      onChange={event => handleOrderNoteFieldChange(event)}
    />
    <SectionDivider gap={16} />
    <OrderDetailsNoteBtn as="button" onClick={() => handleOrderNoteSubmitBtnClick()}>
      {(orderNoteChangeSubmission.status === status.FETCHING && (
        <Loader width={14} height={14} thickness={4} />
      )) ||
        stringsData.orderDetailsContent.orderNoteBtnText}
    </OrderDetailsNoteBtn>
  </OrderDetailsNoteSection>
);
OrderNoteSection.propTypes = {
  stringsData: PropTypes.shape({
    orderDetailsContent: PropTypes.shape({
      orderNoteTitle: PropTypes.string,
      orderNoteTextAreaPlaceholder: PropTypes.string,
      orderNoteBtnText: PropTypes.string,
    }),
  }).isRequired,
  orderNoteChangeSubmission: PropTypes.shape({
    status: PropTypes.string,
  }).isRequired,
  orderNoteValue: PropTypes.string.isRequired,
  handleOrderNoteFieldChange: PropTypes.func.isRequired,
  handleOrderNoteSubmitBtnClick: PropTypes.func.isRequired,
};

export const OrderVisitNoteSection = ({ stringsData, orderDetails }) => {
  const orderData = orderDetails.details;
  const { note } = orderData;

  if (note === '') return '';

  return (
    <OrderDetailsVisitNoteSection data-testid="order-details-visit-note-section">
      <OrderDetailsTitle>{stringsData.orderDetailsContent.visitNote}</OrderDetailsTitle>
      <OrdetVisitNote data-testid="order-details-visit-note-text">
        {orderDetails.details.note}
      </OrdetVisitNote>
    </OrderDetailsVisitNoteSection>
  );
};
OrderVisitNoteSection.propTypes = {
  stringsData: PropTypes.shape({
    orderDetailsContent: PropTypes.shape({
      visitNote: PropTypes.string,
    }),
  }).isRequired,
  orderDetails: PropTypes.shape({
    details: PropTypes.shape({
      note: PropTypes.string,
    }),
  }).isRequired,
};
// ###############################################################
// <-------------------- End Sub Sections ----------------------->
// ###############################################################

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const OrderDetailsComponent = ({
  stringsData,
  orderDetails,
  labAssignDropdownMenuItemsList,
  labAssignDropdownMenuSelectedObj,
  chemistsList,
  usersList,
  insuranceProviders,
  assignOrderToEntityDetails,
  orderServicesSuggestions,
  orderServicesSubmission,
  uploadContentSubmission,
  orderNoteChangeSubmission,
  loginInfo,
  actionPopupStatus,
  handleLabAssignDropdownMenuChange,
  handleOrderRefreshBtnClick,
  handleOrderSendResultsBtnClick,
  getServicesSuggestionsData,
  resetServicesSuggestionsData,
  submitOrderInvoiceDispensedItems,
  actionBtnClick,
  actionPopupDismissBtn,
  actionPopupConfirmBtn,
  actionPopupConfirmBtnCallback,
  updateOrderNote,
  scheduleOrderPopupConfirmBtn,
  cancelOrderPopupConfirmBtn,
  rejectOrderPopupConfirmBtn,
  orderStatusEnum,
  orderStatusToEnableAssignMenu,
  orderStatusToEnableEditInvoice,
  fetchScheduleOrderReasons,
  fetchCancelOrderReasons,
  fetchRejectOrderReasons,
  scheduleOrderReasons,
  cancelOrderReasons,
  rejectOrderReasons,
  orderInfoStickerDetails,
  objectMapper,
  hideButtonBasedOnState,
  mappedOrderStatus,
  editShippingAddress,
  selectItemsSearchApi,
  permissionsCategories,
  showOrderNoteSection,
  showInvoiceStockColumn,
  showInvoiceSubTotalColumn,
  showInvoiceQuantityColumn,
  showOrderInfoSticker,
  showInsuranceDetailsSection,
  showOrderInfoBadges,
  showPaymentMethodSticker,
  showRejectButton,
  showPatientUserDetailsEditBtn,
  showAddressDetailsEditBtn,
}) => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const themeContext = useContext(ThemeContext);
  const orderData = orderDetails?.details;
  const orderStatus = orderData?.orderStateTypeId;
  const attachedResults = orderData?.attachedResults;
  const attachedResultsArr = attachedResults?.attachments || [];
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const [showCopiedTextOverlay, setShowCopiedTextOverlay] = useState(false);
  const [orderResultsArr, setOrderResultsArr] = useState([]);
  const [orderNoteValue, setOrderNoteValue] = useState('');
  const [previewFileIndex, setPreviewFileIndex] = useState(0);
  const [isPreviewFilePopupOpen, setIsPreviewFilePopupOpen] = useState(false);
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  useEffect(() => {
    if (orderDetails.status === status.FETCHING) setOrderResultsArr([]);
  }, [orderDetails]);
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  const togglePreviewImg = (openPopup, file) => {
    if (openPopup) {
      if (file.type === orderResultsTypeEnum.pdf) {
        window.open(file.url);
      } else {
        const fileIndex = attachedResultsArr.indexOf(file);
        setPreviewFileIndex(fileIndex);
        setIsPreviewFilePopupOpen(true);
      }
    } else setIsPreviewFilePopupOpen(false);
  };
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleTextToCopyToClipboardClick = textToCopy => {
    navigator.clipboard.writeText(textToCopy);
    setShowCopiedTextOverlay(true);

    setTimeout(() => {
      setShowCopiedTextOverlay(false);
    }, 1000);
  };

  const handleOrderNoteFieldChange = event => {
    const { value } = event.target;

    setOrderNoteValue(value);
  };

  const handleOrderNoteSubmitBtnClick = () => {
    updateOrderNote(orderNoteValue);
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  if (orderDetails.status === status.SHOULD_CALL_API || orderDetails.status === status.FETCHING)
    return (
      <LoaderContainer>
        <Loader />
      </LoaderContainer>
    );

  if (orderDetails.status === status.FAIL)
    return (
      <LoaderContainer>
        <OrderDetailsNoDataText>
          {stringsData.orderDetailsContent.noDataPlaceholder}
        </OrderDetailsNoDataText>
      </LoaderContainer>
    );

  return (
    <OrderDetailsWrapper>
      <OrderDetailsHeader
        stringsData={stringsData}
        orderDetails={orderDetails}
        loginInfo={loginInfo}
        actionPopupStatus={actionPopupStatus}
        handleOrderRefreshBtnClick={handleOrderRefreshBtnClick}
        actionBtnClick={actionBtnClick}
        actionPopupDismissBtn={actionPopupDismissBtn}
        actionPopupConfirmBtn={actionPopupConfirmBtn}
        actionPopupConfirmBtnCallback={actionPopupConfirmBtnCallback}
        handleTextToCopyToClipboardClick={handleTextToCopyToClipboardClick}
        scheduleOrderPopupConfirmBtn={scheduleOrderPopupConfirmBtn}
        cancelOrderPopupConfirmBtn={cancelOrderPopupConfirmBtn}
        rejectOrderPopupConfirmBtn={rejectOrderPopupConfirmBtn}
        orderStatusEnum={orderStatusEnum}
        fetchScheduleOrderReasons={fetchScheduleOrderReasons}
        fetchCancelOrderReasons={fetchCancelOrderReasons}
        fetchRejectOrderReasons={fetchRejectOrderReasons}
        scheduleOrderReasons={scheduleOrderReasons}
        cancelOrderReasons={cancelOrderReasons}
        rejectOrderReasons={rejectOrderReasons}
        hideButtonBasedOnState={hideButtonBasedOnState}
        selectItemsSearchApi={selectItemsSearchApi}
        showRejectButton={showRejectButton}
      />
      <OrderDetailsContent>
        <OrderDetailsContentFirstColumn>
          <Can
            permissionsCategory={permissionsCategories?.order}
            fallBackPermissions={[
              'order:viewShippingAddress',
              'order:viewPatientDetails',
              'order:viewInsuranceDetails',
            ]}
            renderYes={() => (
              <OrderInfoSection
                themeContext={themeContext}
                stringsData={stringsData}
                orderDetails={orderDetails}
                handleTextToCopyToClipboardClick={handleTextToCopyToClipboardClick}
                insuranceProviders={insuranceProviders}
                orderInfoStickerDetails={orderInfoStickerDetails}
                showOrderInfoSticker={showOrderInfoSticker}
                showInsuranceDetailsSection={showInsuranceDetailsSection}
                showOrderInfoBadges={showOrderInfoBadges}
                showPaymentMethodSticker={showPaymentMethodSticker}
                showPatientUserDetailsEditBtn={showPatientUserDetailsEditBtn}
                showAddressDetailsEditBtn={showAddressDetailsEditBtn}
                editShippingAddress={editShippingAddress}
                permissionsCategories={permissionsCategories}
              />
            )}
            no={() => ''}
          />
          <Can
            permissionsCategory={permissionsCategories?.order}
            perform="order:viewOrderLogs"
            renderYes={() => (
              <>
                <SectionDivider gap={16} />
                <OrderLogsSection
                  themeContext={themeContext}
                  stringsData={stringsData}
                  orderDetails={orderDetails}
                  labsList={labAssignDropdownMenuItemsList}
                  chemistsList={chemistsList}
                  usersList={usersList}
                  orderStatusEnum={orderStatusEnum}
                />
              </>
            )}
            renderNo={() => null}
          />
        </OrderDetailsContentFirstColumn>
        <OrderDetailsContentSecondColumn data-testid="order-details-content-second-section">
          <OrderStatusSection mappedOrderStatus={mappedOrderStatus} />
          {orderStatusToEnableAssignMenu?.includes(orderStatus) && (
            <Can
              permissionsCategory={permissionsCategories?.order}
              fallBackPermissions={['order:assignPharmacy', 'order:assignDelivery']}
              renderYes={() => (
                <>
                  <SectionDivider gap={16} />
                  <OrderAssignedEntitySection
                    stringsData={stringsData}
                    orderDetails={orderDetails}
                    labAssignDropdownMenuItemsList={labAssignDropdownMenuItemsList}
                    labAssignDropdownMenuSelectedObj={labAssignDropdownMenuSelectedObj}
                    assignOrderToEntityDetails={assignOrderToEntityDetails}
                    handleLabAssignDropdownMenuChange={handleLabAssignDropdownMenuChange}
                  />
                </>
              )}
              renderNo={() => null}
            />
          )}
          <SectionDivider gap={16} />
          <OrderVisitNoteSection stringsData={stringsData} orderDetails={orderDetails} />
          <Can
            permissionsCategory={permissionsCategories?.order}
            perform="order:viewOrderAttachements"
            renderYes={() => (
              <>
                <SectionDivider gap={16} />
                <OrderAttachmentSection
                  themeContext={themeContext}
                  stringsData={stringsData}
                  orderDetails={orderDetails}
                />
              </>
            )}
            renderNo={() => null}
          />
          <Can
            permissionsCategory={permissionsCategories?.order}
            fallBackPermissions={['order:viewOrderItems', 'order:editOrderItems']}
            renderYes={() => (
              <>
                <SectionDivider gap={16} />
                <OrderInvoice
                  stringsData={stringsData}
                  orderDetails={orderDetails}
                  orderServicesSuggestions={orderServicesSuggestions}
                  orderServicesSubmission={orderServicesSubmission}
                  getServicesSuggestionsData={getServicesSuggestionsData}
                  resetServicesSuggestionsData={resetServicesSuggestionsData}
                  submitOrderInvoiceDispensedItems={submitOrderInvoiceDispensedItems}
                  showInvoiceStockColumn={showInvoiceStockColumn}
                  showInvoiceSubTotalColumn={showInvoiceSubTotalColumn}
                  showInvoiceQuantityColumn={showInvoiceQuantityColumn}
                  orderStatusToEnableEditInvoice={orderStatusToEnableEditInvoice}
                  objectMapper={objectMapper}
                />
              </>
            )}
            renderNo={() => null}
          />
          {orderStatusToShowUploadedResultsSection.includes(orderStatus) && (
            <>
              <SectionDivider gap={16} />
              <OrderAttachResultsSection
                themeContext={themeContext}
                stringsData={stringsData}
                orderDetails={orderDetails}
                uploadContentSubmission={uploadContentSubmission}
                orderResultsArr={orderResultsArr}
                previewFileIndex={previewFileIndex}
                isPreviewFilePopupOpen={isPreviewFilePopupOpen}
                setOrderResultsArr={setOrderResultsArr}
                togglePreviewImg={togglePreviewImg}
                handleTextToCopyToClipboardClick={handleTextToCopyToClipboardClick}
                handleOrderSendResultsBtnClick={handleOrderSendResultsBtnClick}
                orderStatusEnum={orderStatusEnum}
              />
            </>
          )}
          {showOrderNoteSection && (
            <Can
              permissionsCategory={permissionsCategories?.order}
              perform="order:editNotes"
              renderYes={() => (
                <>
                  <SectionDivider gap={16} />
                  <OrderNoteSection
                    stringsData={stringsData}
                    orderNoteChangeSubmission={orderNoteChangeSubmission}
                    orderNoteValue={orderNoteValue}
                    handleOrderNoteFieldChange={handleOrderNoteFieldChange}
                    handleOrderNoteSubmitBtnClick={handleOrderNoteSubmitBtnClick}
                  />
                </>
              )}
              renderNo={() => null}
            />
          )}
          {showCopiedTextOverlay && (
            <OrderNumberOverlay>
              {stringsData.orderDetailsContent.copiedToClipboardText}
            </OrderNumberOverlay>
          )}
        </OrderDetailsContentSecondColumn>
      </OrderDetailsContent>
    </OrderDetailsWrapper>
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

OrderDetailsComponent.propTypes = {
  stringsData: PropTypes.shape({
    orderDetailsContent: PropTypes.shape({
      noDataPlaceholder: PropTypes.string,
      copiedToClipboardText: PropTypes.string,
    }),
  }).isRequired,
  orderDetails: PropTypes.shape({
    status: PropTypes.string,
    details: PropTypes.shape({
      orderNumber: PropTypes.string,
      orderStateTypeId: PropTypes.number,
    }),
  }).isRequired,
  labAssignDropdownMenuItemsList: PropTypes.shape({
    status: PropTypes.string,
  }).isRequired,
  labAssignDropdownMenuSelectedObj: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})])
    .isRequired,
  chemistsList: PropTypes.shape({
    details: PropTypes.shape({
      results: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }).isRequired,
  usersList: PropTypes.shape({
    details: PropTypes.shape({
      results: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }).isRequired,
  insuranceProviders: PropTypes.arrayOf(PropTypes.shape({})),
  assignOrderToEntityDetails: PropTypes.shape({}).isRequired,
  orderServicesSuggestions: PropTypes.shape({}).isRequired,
  orderServicesSubmission: PropTypes.shape({}).isRequired,
  uploadContentSubmission: PropTypes.shape({}).isRequired,
  orderNoteChangeSubmission: PropTypes.shape({}).isRequired,
  loginInfo: PropTypes.shape({}).isRequired,
  actionPopupStatus: PropTypes.number.isRequired,
  showOrderNoteSection: PropTypes.bool,
  showInvoiceStockColumn: PropTypes.bool,
  showInvoiceSubTotalColumn: PropTypes.bool,
  showInvoiceQuantityColumn: PropTypes.bool,
  showOrderInfoSticker: PropTypes.bool,
  showInsuranceDetailsSection: PropTypes.bool,
  showOrderInfoBadges: PropTypes.bool,
  showPaymentMethodSticker: PropTypes.bool,
  showRejectButton: PropTypes.bool,
  showPatientUserDetailsEditBtn: PropTypes.bool,
  showAddressDetailsEditBtn: PropTypes.bool,
  handleLabAssignDropdownMenuChange: PropTypes.func.isRequired,
  handleOrderRefreshBtnClick: PropTypes.func.isRequired,
  handleOrderSendResultsBtnClick: PropTypes.func.isRequired,
  getServicesSuggestionsData: PropTypes.func.isRequired,
  resetServicesSuggestionsData: PropTypes.func.isRequired,
  submitOrderInvoiceDispensedItems: PropTypes.func.isRequired,
  actionBtnClick: PropTypes.func.isRequired,
  actionPopupDismissBtn: PropTypes.func.isRequired,
  actionPopupConfirmBtn: PropTypes.func.isRequired,
  actionPopupConfirmBtnCallback: PropTypes.func.isRequired,
  cancelOrderPopupConfirmBtn: PropTypes.func.isRequired,
  rejectOrderPopupConfirmBtn: PropTypes.func,
  orderStatusEnum: PropTypes.shape({}).isRequired,
  orderStatusToEnableAssignMenu: PropTypes.arrayOf(PropTypes.number).isRequired,
  orderStatusToEnableEditInvoice: PropTypes.arrayOf(PropTypes.number).isRequired,
  updateOrderNote: PropTypes.func.isRequired,
  scheduleOrderPopupConfirmBtn: PropTypes.func.isRequired,
  fetchScheduleOrderReasons: PropTypes.func.isRequired,
  fetchCancelOrderReasons: PropTypes.func.isRequired,
  fetchRejectOrderReasons: PropTypes.func,
  scheduleOrderReasons: PropTypes.shape({}).isRequired,
  cancelOrderReasons: PropTypes.shape({}).isRequired,
  rejectOrderReasons: PropTypes.shape({}),
  orderInfoStickerDetails: PropTypes.shape({}),
  objectMapper: PropTypes.func.isRequired,
  hideButtonBasedOnState: PropTypes.shape({}).isRequired,
  mappedOrderStatus: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  editShippingAddress: PropTypes.func,
  selectItemsSearchApi: PropTypes.func,
  permissionsCategories: PropTypes.shape({
    order: PropTypes.string,
  }),
};

OrderDetailsComponent.defaultProps = {
  showOrderNoteSection: false,
  showInvoiceStockColumn: false,
  showInvoiceSubTotalColumn: false,
  showInvoiceQuantityColumn: false,
  showOrderInfoSticker: false,
  showInsuranceDetailsSection: false,
  showOrderInfoBadges: false,
  showPaymentMethodSticker: false,
  showRejectButton: false,
  showPatientUserDetailsEditBtn: false,
  showAddressDetailsEditBtn: false,
  insuranceProviders: [],
  orderInfoStickerDetails: {},
  rejectOrderReasons: {},
  rejectOrderPopupConfirmBtn: () => {},
  fetchRejectOrderReasons: () => {},
  editShippingAddress: () => {},
  selectItemsSearchApi: () => {},
  permissionsCategories: {},
};

export default OrderDetailsComponent;
